import { surfaceBase } from "@web/components/ui";
import { useEffect, useState } from "react";
import { useToast } from "@/contexts";
import { createMyInvite, listMyInvites, type OwnedAccessInvite } from "@/features/access/api";

export default function InvitesSection() {
  const toast = useToast();
  const [invites, setInvites] = useState<OwnedAccessInvite[] | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await listMyInvites();
      if (data) setInvites(data);
    })();
  }, []);

  async function handleCreate() {
    if (creating) return;
    setCreating(true);
    const { data, error } = await createMyInvite();
    setCreating(false);
    if (error) {
      toast.showAlert("Couldn't create invite", error.message, "error");
      return;
    }
    if (data) {
      const { data: refreshed } = await listMyInvites();
      if (refreshed) setInvites(refreshed);
    }
  }

  async function handleCopy(code: string) {
    const url = `${window.location.origin}/sign-up?code=${encodeURIComponent(code)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.showAlert("Copied", "Invite link copied to your clipboard.", "success");
    } catch {
      toast.showAlert("Couldn't copy", "Copy this link manually instead.", "error");
    }
  }

  const pending = invites?.filter((i) => !i.redeemedAt) ?? [];
  const redeemed = invites?.filter((i) => i.redeemedAt) ?? [];

  return (
    <div className={`${surfaceBase} bg-card p-5`}>
      <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">Invites</span>
      <p className="text-text-muted text-[0.8125rem] mt-2 leading-5">
        FootSlapping is invite-only. Share a code to bring friends in.
      </p>

      {invites === null ? (
        <p className="text-text-muted text-[0.8125rem] mt-4">Loading...</p>
      ) : (
        <div className="flex flex-col gap-3 mt-4">
          {pending.map((invite) => (
            <div
              key={invite.code}
              className="flex items-center justify-between gap-3 py-2 border-b border-[rgba(28,27,46,0.08)] last:border-b-0"
            >
              <span className="text-text text-[0.875rem] font-mono font-bold">{invite.code}</span>
              <button
                type="button"
                onClick={() => handleCopy(invite.code)}
                className="py-1.5 px-3 border border-[rgba(28,27,46,0.2)] rounded-[12px] bg-white text-text-muted text-[0.75rem] font-bold cursor-pointer"
              >
                Copy link
              </button>
            </div>
          ))}

          {redeemed.length > 0 && (
            <div className="flex flex-col gap-1.5 pt-1">
              <span className="text-text-dim text-[0.6875rem] font-bold uppercase tracking-[0.0313em]">
                Redeemed
              </span>
              {redeemed.map((invite) => (
                <div key={invite.code} className="flex items-center justify-between gap-3 py-1">
                  <span className="text-text-muted text-[0.8125rem]">
                    {invite.redeemedByName ?? "Someone"} joined
                  </span>
                </div>
              ))}
            </div>
          )}

          {pending.length === 0 && redeemed.length === 0 && (
            <p className="text-text-muted text-[0.8125rem]">You haven't created any invites yet.</p>
          )}

          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="mt-1 bg-aqua rounded-[16px] py-2.5 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer disabled:opacity-50"
          >
            {creating ? "Creating..." : "Create invite"}
          </button>
        </div>
      )}
    </div>
  );
}
